import { FC, useState } from "react";
import { canDeleteEntity } from "@common/helpers/canDeleteEntity";
import { Button } from "@design-system/Button/Button";
import { StandardModal } from "@design-system/Modal/Modal";
import { SectionHeader } from "@design-system/Section/Section";
import { DeleteEntityModal } from "@components/entity/DeleteEntityModal";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { useEntities } from "@hooks/useEntities";
import { useUser } from "@hooks/useUser";
import { SettingRow } from "../General/SettingRow";
import { useSettings } from "../SettingsProvider";
import { useEntity } from "../../entity/useEntity";
import styles from "./EntityDeleteSection.module.scss";

export const EntityDeleteSection: FC = () => {
    const entity = useEntity();
    const user = useUser();
    const workspaceRole = useActiveWorkspaceRole();
    const businessEntities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const { close: closeSettings } = useSettings();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isEligibleEntity = !entity.isPersonal && !entity.isMockEntity;
    const canUserDelete = canDeleteEntity({
        userType: user.type,
        workspaceRole,
    });
    if (!isEligibleEntity || !canUserDelete) {
        return null;
    }

    const hasOtherBusinessEntity = businessEntities.some(
        (businessEntity) => businessEntity.id !== entity.id,
    );
    const disabledReason = hasOtherBusinessEntity
        ? "Permanently remove this entity and its financial records."
        : "The only business entity in a workspace cannot be deleted.";

    return (
        <section>
            <SectionHeader>
                <h4>Delete entity</h4>
            </SectionHeader>
            <div className={styles.settingsContainer}>
                <SettingRow
                    label="Delete entity"
                    value={disabledReason}
                    renderButton={() => (
                        <Button
                            variant="destructive"
                            disabled={!hasOtherBusinessEntity}
                            onClick={() => setIsModalOpen(true)}
                        >
                            Delete entity
                        </Button>
                    )}
                />
            </div>
            <StandardModal
                show={isModalOpen}
                onHide={() => setIsModalOpen(false)}
                size="md"
            >
                <DeleteEntityModal
                    entity={entity}
                    onClose={() => setIsModalOpen(false)}
                    onDeleted={() => closeSettings()}
                />
            </StandardModal>
        </section>
    );
};
